import React, { useState, useEffect, useMemo } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import PropTypes from "prop-types";
import { AreaChartIcon, BarChartIcon, LineChartIcon } from "../SvgIcon";
import { monthNames } from "../../../utils/constants";

const CustomTooltip = ({ active, payload, label, currentYear, selectedMetric }) => {
  if (active && payload && payload.length) {
    const currentValue =
      payload.find((p) => p.dataKey === "current")?.value || 0;
    const previousValue =
      payload.find((p) => p.dataKey === "previous")?.value || 0;

    const inverseMetrics = ["notQuoted", "refunded"];
    const isInverse = inverseMetrics.includes(selectedMetric);

    const calculatePercentage = (curr, prev, isInv = false) => {
      if (prev === 0) return { percent: "0", isPositive: false, symbol: "" };
      const percent = (((curr - prev) / prev) * 100).toFixed(1);
      const pctValue = parseFloat(percent);
      const isPositive = isInv ? pctValue <= 0 : pctValue >= 0;
      return {
        percent: Math.abs(pctValue).toFixed(1),
        isPositive,
        symbol: isPositive ? "↑" : "↓",
      };
    };

    const isFutureMonth = currentValue === 0 && currentYear === new Date().getFullYear() &&
                          new Date().getMonth() < monthNames.indexOf(label);
    const comparison = calculatePercentage(currentValue, previousValue, isInverse);

    return (
      <div className="chart-tooltip">
        <p className="chart-tooltip__title">{label}</p>
        <div className="chart-tooltip__content">
          {payload.map((entry, index) => {
            const colorClass =
              entry.dataKey === "current" ? "current" : "previous";

            return (
              <div key={index} className="chart-tooltip__entry">
                <span className="chart-tooltip__label">{entry.name}:</span>
                <span
                  className={`chart-tooltip__value chart-tooltip__value--${colorClass}`}
                >
                  {typeof entry.value === "number"
                    ? entry.value.toFixed(0)
                    : entry.value}
                </span>
              </div>
            );
          })}
        </div>
        {!isFutureMonth && (
          <div className="chart-tooltip__comparison">
            <span className="comparison-year-label">
              {currentYear - 1} vs {currentYear}:
            </span>
            <span
              className={`comparison-text ${comparison.isPositive ? "positive" : "negative"}`}
            >
              {comparison.symbol} {comparison.percent}%
            </span>
          </div>
        )}
      </div>
    );
  }
  return null;
};

const RptMonthlyCharts = ({
  currentYearData,
  previousYearData,
  currentYear,
  selectedMetric,
  setSelectedMetric,
  chartType,
  setChartType,
}) => {
  const [chartData, setChartData] = useState([]);

  const metricsConfig = {
    driverCount: { label: "Drivers", key: "driverCount" },
    userCount: { label: "Users", key: "userCount" },
    jobCount: { label: "Jobs", key: "jobCount" },
    totalQuotes: { label: "Quotes", key: "totalQuotes" },
    notQuoted: { label: "Not Quoted", key: "notQuoted" },
    booked: { label: "Booked", key: "booked" },
    refunded: { label: "Refunds", key: "refunded" },
    jobTotalRatings: { label: "Total Reviews", key: "jobTotalRatings" },
    bookedPercentage: { label: "Conversion Rate", key: "bookedPercentage" },
  };

  useEffect(() => {
    const newChartData = [];
    for (let i = 0; i < 12; i++) {
      const currData =
        currentYearData?.[i] ||
        currentYearData?.[String(i)] ||
        currentYearData?.[i + 1] ||
        currentYearData?.[String(i + 1)] ||
        {};
      const prevData =
        previousYearData?.[i] ||
        previousYearData?.[String(i)] ||
        previousYearData?.[i + 1] ||
        previousYearData?.[String(i + 1)] ||
        {};

      newChartData.push({
        month: monthNames[i],
        [`${currentYear} ${selectedMetric}`]: currData[selectedMetric] || 0,
        [`${currentYear - 1} ${selectedMetric}`]: prevData[selectedMetric] || 0,
        current: currData[selectedMetric] || 0,
        previous: prevData[selectedMetric] || 0,
      });
    }
    setChartData(newChartData);
  }, [currentYearData, previousYearData, currentYear, selectedMetric]);

  return (
    <>
      <div className="rpt-charts-container">
        <div className="charts-controls">
          <div className="controls-left">
            <label htmlFor="metric-select" className="select-metric-label">
              Select Metric:
            </label>
            <select
              id="metric-select"
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="metric-selector"
            >
              {Object.entries(metricsConfig).map(([key, config]) => (
                <option key={key} value={key}>
                  {config.label}
                </option>
              ))}
            </select>
          </div>

          <div className="chart-type-tabs">
            <button
              className={`chart-tab ${chartType === "bar" ? "active" : ""}`}
              onClick={() => setChartType("bar")}
              title="Bar Chart"
            >
              <BarChartIcon />
            </button>
            <button
              className={`chart-tab ${chartType === "line" ? "active" : ""}`}
              onClick={() => setChartType("line")}
              title="Line Chart"
            >
              <LineChartIcon />{" "}
            </button>

            <button
              className={`chart-tab ${chartType === "area" ? "active" : ""}`}
              onClick={() => setChartType("area")}
              title="Area Chart"
            >
              <AreaChartIcon />{" "}
            </button>
          </div>
        </div>

        <div className="chart-wrapper">
          <div className="chart-header">
            <h3>
              Year over Year Comparison : {currentYear - 1} vs {currentYear}
            </h3>
            <p className="chart-description">Monthly comparison over time</p>
          </div>

          <ResponsiveContainer width="100%" height={420}>
            {chartType === "line" && (
              <LineChart
                data={chartData}
                margin={{ top: 15, right: 30, left: 40, bottom: 80 }}
              >
                <defs>
                  <linearGradient id="colorCurrent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0d9488" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#0d9488" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient
                    id="colorPrevious"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#e5e7eb"
                  vertical={false}
                />
                <XAxis
                  dataKey="month"
                  stroke="#9ca3af"
                  className="chart-axis-x"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={0}
                />
                <YAxis stroke="#9ca3af" className="chart-axis-y" width={50} />
                <Tooltip
                  content={<CustomTooltip currentYear={currentYear} selectedMetric={selectedMetric} />}
                />
                <Legend wrapperClassName="chart-legend" iconType="line" />
                <Line
                  type="monotone"
                  dataKey="previous"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  dot={{ fill: "#3b82f6", r: 5 }}
                  activeDot={{ r: 7 }}
                  name={`${currentYear - 1}`}
                  isAnimationActive={true}
                />
                <Line
                  type="monotone"
                  dataKey="current"
                  stroke="#0d9488"
                  strokeWidth={3}
                  dot={{ fill: "#0d9488", r: 5 }}
                  activeDot={{ r: 7 }}
                  name={`${currentYear}`}
                  isAnimationActive={true}
                />
              </LineChart>
            )}

            {chartType === "bar" && (
              <BarChart
                data={chartData}
                margin={{ top: 15, right: 30, left: 40, bottom: 80 }}
              >
                <defs>
                  <linearGradient id="barCurrent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={1} />
                    <stop offset="100%" stopColor="#0d9488" stopOpacity={1} />
                  </linearGradient>
                  <linearGradient id="barPrevious" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#60a5fa" stopOpacity={1} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={1} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#e5e7eb"
                  vertical={false}
                />
                <XAxis
                  dataKey="month"
                  stroke="#9ca3af"
                  className="chart-axis-x"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={0}
                />
                <YAxis stroke="#9ca3af" className="chart-axis-y" width={50} />
                <Tooltip
                  content={<CustomTooltip currentYear={currentYear} selectedMetric={selectedMetric} />}
                />
                <Legend wrapperClassName="chart-legend" />
                <Bar
                  dataKey="previous"
                  fill="url(#barPrevious)"
                  radius={[6, 6, 0, 0]}
                  name={`${currentYear - 1}`}
                />
                <Bar
                  dataKey="current"
                  fill="url(#barCurrent)"
                  radius={[6, 6, 0, 0]}
                  name={`${currentYear}`}
                />
              </BarChart>
            )}

            {chartType === "area" && (
              <AreaChart
                data={chartData}
                margin={{ top: 15, right: 30, left: 40, bottom: 80 }}
              >
                <defs>
                  <linearGradient id="areaCurrent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0d9488" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#0d9488" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="areaPrevious" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#e5e7eb"
                  vertical={false}
                />
                <XAxis
                  dataKey="month"
                  stroke="#9ca3af"
                  className="chart-axis-x"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={0}
                />
                <YAxis stroke="#9ca3af" className="chart-axis-y" width={50} />
                <Tooltip
                  content={<CustomTooltip currentYear={currentYear} selectedMetric={selectedMetric} />}
                />
                <Legend wrapperClassName="chart-legend" />
                <Area
                  type="monotone"
                  dataKey="previous"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fill="url(#areaPrevious)"
                  name={`${currentYear - 1}`}
                  isAnimationActive={true}
                />
                <Area
                  type="monotone"
                  dataKey="current"
                  stroke="#0d9488"
                  strokeWidth={2}
                  fill="url(#areaCurrent)"
                  name={`${currentYear}`}
                  isAnimationActive={true}
                />
              </AreaChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
};

RptMonthlyCharts.propTypes = {
  currentYearData: PropTypes.object.isRequired,
  previousYearData: PropTypes.object.isRequired,
  currentYear: PropTypes.number.isRequired,
  selectedMetric: PropTypes.string.isRequired,
  setSelectedMetric: PropTypes.func.isRequired,
  chartType: PropTypes.string.isRequired,
  setChartType: PropTypes.func.isRequired,
};

export default RptMonthlyCharts;
